<template>
  <div class="panel">
    <div class="panel__content">
      <!-- Panel header -->
      <div v-if="panel" class="panel__header">
        <a :href="panel.href" class="panel__title">{{ panel.title }}</a>
        <div v-if="panel.progress" class="panel__progress-sub">
          {{ panel.progress.completed }} of {{ panel.progress.total }} completed
        </div>
        <div v-if="panel.progress" class="panel__progress-bar">
          <div class="panel__progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
      </div>

      <!-- Panel nav links -->
      <div class="panel__nav" v-if="panel">
        <template v-for="link in panel.links" :key="link.href">
          <!-- Collapsible group (modules with sub_links) -->
          <div v-if="link.sub_links && link.sub_links.length" class="panel__group">
            <button
              class="panel__group-toggle"
              :class="{ 'panel__group-toggle--active': isActive(link.href) }"
              @click="toggleGroup(link.href)"
              @dblclick="navigateGroup(link.href)"
            >
              <span class="panel__group-label">{{ link.label }}</span>
              <i
                class="fas fa-chevron-right panel__group-chevron"
                :class="{ 'panel__group-chevron--open': expandedGroups[link.href] }"
              ></i>
            </button>
            <div v-if="expandedGroups[link.href]" class="panel__group-children">
              <a
                v-for="sub in link.sub_links"
                :key="sub.href"
                :href="sub.href"
                class="panel__item"
                :class="{ 'panel__item--active': isActive(sub.href) }"
              >
                <i v-if="sub.icon" :class="['fas', sub.icon]"></i>
                <span class="panel__item-label">{{ sub.label }}</span>
                <span v-if="sub.badge" class="panel__item-count" :class="{ 'panel__item-count--notify': typeof sub.badge === 'number' }">{{ sub.badge }}</span>
              </a>
            </div>
          </div>
          <!-- Simple link without sub_links -->
          <a
            v-if="!link.sub_links || link.sub_links.length === 0"
            :href="link.href"
            class="panel__item"
            :class="{ 'panel__item--active': isActive(link.href) }"
            @click="handleLinkClick($event, link.href)"
          >
            <i v-if="link.icon" :class="['fas', link.icon]"></i>
            <span class="panel__item-label">{{ link.label }}</span>
            <span v-if="link.badge" class="panel__item-count" :class="{ 'panel__item-count--notify': typeof link.badge === 'number' }">{{ link.badge }}</span>
          </a>
        </template>
      </div>
    </div>

    <!-- Footer -->
    <div class="panel__footer">
      <div class="panel__copyright">
        <div>&copy; {{ currentYear }} What's My Theology</div>
        <div class="panel__legal-links">
          <a href="/privacy">Privacy</a>
          <span class="panel__separator">&middot;</span>
          <a href="/terms">Terms</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  panel: Object
})

const STORAGE_KEY = 'navPanelExpandedGroups'

const currentYear = new Date().getFullYear()
const currentPath = ref(window.location.pathname)
const expandedGroups = ref(loadExpandedGroups())
let clickTimers = {}

function loadExpandedGroups() {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored) {
    try { return JSON.parse(stored) } catch { /* fall through */ }
  }
  return null // signals first visit — handled after panel data arrives
}

function saveExpandedGroups() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedGroups.value))
}

function updatePath() {
  currentPath.value = window.location.pathname
  autoExpandActiveGroup()
}

// On first visit (nothing stored), expand all groups.
// Otherwise, just ensure the active group is open.
function autoExpandActiveGroup() {
  if (!props.panel?.links) return
  const isFirstVisit = expandedGroups.value === null
  if (isFirstVisit) expandedGroups.value = {}

  for (const link of props.panel.links) {
    if (!link.sub_links?.length) continue
    if (isFirstVisit) {
      expandedGroups.value[link.href] = true
    } else {
      const hasActive = link.sub_links.some(sub => isActive(sub.href))
      if (hasActive) expandedGroups.value[link.href] = true
    }
  }
  saveExpandedGroups()
}

function toggleGroup(href) {
  if (clickTimers[href]) return // ignore second click of a double-click
  clickTimers[href] = setTimeout(() => {
    delete clickTimers[href]
    expandedGroups.value[href] = !expandedGroups.value[href]
    saveExpandedGroups()
  }, 250)
}

function navigateGroup(href) {
  clearTimeout(clickTimers[href])
  delete clickTimers[href]
  expandedGroups.value[href] = true
  saveExpandedGroups()
  window.Turbo?.visit(href) || (window.location.href = href)
}

// Re-compute expanded groups when panel data changes
watch(() => props.panel, () => {
  autoExpandActiveGroup()
}, { immediate: true })

onMounted(() => {
  document.addEventListener('turbo:load', updatePath)
  autoExpandActiveGroup()
})

onUnmounted(() => {
  document.removeEventListener('turbo:load', updatePath)
})

const progressPercent = computed(() => {
  if (!props.panel?.progress) return 0
  const { completed, total } = props.panel.progress
  return total > 0 ? Math.round((completed / total) * 100) : 0
})

function isActive(href) {
  if (!href) return false
  const cleanHref = href.split('#')[0]
  return currentPath.value === cleanHref || currentPath.value.startsWith(cleanHref + '/')
}

function handleLinkClick(event, href) {
  if (!href) return
  const [path, hash] = href.split('#')
  if (hash && path === currentPath.value) {
    event.preventDefault()
    const target = document.getElementById(hash)
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' })
      window.history.replaceState(null, '', `${path}#${hash}`)
    }
  }
}
</script>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
}

.panel__content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

/* ── Header ── */
.panel__header {
  padding: 14px 12px;
  border-bottom: 1px solid var(--rail-divider);
}

.panel__title {
  display: block;
  font-size: var(--font-down-1);
  font-weight: 700;
  color: var(--rail-text-strong);
  text-decoration: none;
  margin-bottom: 4px;
}

.panel__title:hover {
  color: var(--tertiary);
}

.panel__progress-sub {
  font-size: 0.6875em;
  color: var(--rail-text);
}

.panel__progress-bar {
  height: 3px;
  background: var(--rail-divider);
  border-radius: 2px;
  margin-top: 8px;
  overflow: hidden;
}

.panel__progress-fill {
  height: 100%;
  background: var(--tertiary);
  border-radius: 2px;
  transition: width 0.4s ease;
}

/* ── Nav items ── */
.panel__nav {
  padding: 6px 8px;
}

/* ── Collapsible group ── */
.panel__group {
  margin-bottom: 2px;
}

.panel__group-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 8px 4px;
  border: none;
  border-radius: 0;
  background: none;
  font-size: var(--font-down-1);
  font-weight: 600;
  color: var(--rail-text-strong);
  cursor: pointer;
  text-align: left;
}

.panel__group-toggle:hover {
  color: var(--rail-text-strong);
}

.panel__group-toggle--active {
  color: var(--rail-text-strong);
}

.panel__group-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--font-up-1); /* 16px vs group-toggle (14px) */
  font-weight: 600;
}

.panel__group-chevron {
  margin-left: auto;
  font-size: 0.646em; /* 9px vs group-toggle (14px) */
  transition: transform 0.2s ease;
  opacity: 0.5;
}

.panel__group-chevron--open {
  transform: rotate(90deg);
}

.panel__group-children {
  margin-left: 4px;
  padding-left: 12px;
  border-left: 2px solid var(--rail-group-rail);
  margin-top: 2px;
  margin-bottom: 4px;
}

.panel__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border-radius: var(--radius-md);
  font-size: var(--font-down-1);
  font-weight: 500;
  color: var(--rail-text);
  text-decoration: none;
  cursor: pointer;
  transition: background 0.12s;
}

.panel__item:hover {
  background: var(--rail-panel-item-bg-hover);
}

.panel__item--active {
  background: var(--rail-item-bg-active);
  color: var(--rail-text-strong);
}

.panel__item--active:hover {
  background: var(--rail-item-bg-active-hover);
}

.panel__item i {
  width: 16px;
  text-align: center;
  font-size: 0.862em; /* 12px vs __item (14px) */
  color: var(--rail-text);
}

.panel__item-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panel__item-count {
  margin-left: auto;
  font-size: 0.718em; /* 10px vs __item (14px) */
  color: var(--rail-text-faint);
  flex-shrink: 0;
}

.panel__item-count--notify {
  color: var(--rail-on-accent);
  font-weight: 700;
}

.panel__item--active .panel__item-count {
  color: var(--rail-text-strong);
  opacity: 0.5;
}

.panel__item--active .panel__item-count--notify {
  color: var(--rail-on-accent);
  opacity: 1;
}

/* ── Footer ── */
.panel__footer {
  padding: 0.75em 0.5em;
  border-top: 1px solid var(--rail-divider);
  display: flex;
  flex-direction: column;
  gap: 0.25em;
}

.panel__copyright {
  padding: 0.5em 0.5em 0;
  font-size: var(--font-down-3);
  color: var(--rail-text-faint);
}

.panel__legal-links {
  display: flex;
  gap: 0.25em;
  margin-top: 0.15em;
}

.panel__legal-links a {
  color: var(--rail-text-faint);
  text-decoration: none;
}

.panel__legal-links a:hover {
  color: var(--rail-text-strong);
}

.panel__separator {
  opacity: 0.6;
}
</style>
